import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, ArrowRight, ArrowLeft, Send, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import BillUploader from "@/components/BillUploader";
import QuoteAssistant from "@/components/QuoteAssistant";
import { useSiteContent } from "@/lib/useSiteContent";
import { keysForMany } from "@/config/cmsSections";

export default function QuoteForm() {
    const c = useSiteContent(keysForMany("common", "quote-form"));
    const [step, setStep] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [searchParams] = useSearchParams();

    const STEPS = [
        { id: 1, label: c("quote.step1.label", "Type de demande") },
        { id: 2, label: c("quote.step2.label", "Vos coordonnées") },
        { id: 3, label: c("quote.step3.label", "Votre consommation") },
        { id: 4, label: c("quote.step4.label", "Confirmation") },
    ];

    // Pré-remplit l'audience depuis ?audience=particulier|entreprise (utilisé
    // par les cartes "Deux profils" de la home et autres CTA contextuels).
    // Si présent → on verrouille l'audience (le toggle est masqué) pour garder
    // le visiteur dans son parcours d'origine sans switch accidentel.
    const audienceParam = (searchParams.get("audience") || "").toLowerCase();
    const audienceLocked = ["particulier", "b2c", "entreprise", "b2b", "pro"].includes(audienceParam);
    const initialAudience = (() => {
        if (audienceParam === "entreprise" || audienceParam === "b2b" || audienceParam === "pro") {
            return "entreprise";
        }
        if (audienceParam === "particulier" || audienceParam === "b2c") {
            return "particulier";
        }
        return "particulier"; // par défaut quand pas de param
    })();

    const [form, setForm] = useState({
        audience: initialAudience,
        energy_type: "electricity",
        full_name: "",
        email: "",
        phone: "",
        company: "",
        siret: "",
        pdl: "",         // numéro PDL (élec) — pertinent en B2B
        pce: "",         // numéro PCE (gaz) — pertinent en B2B
        revenu_fiscal: "",  // pour qualif aides (particuliers)
        annual_kwh: "",
        postal_code: "",
        message: "",
        cee_interest: false,
    });

    const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

    const next = () => setStep((s) => Math.min(s + 1, STEPS.length));
    const prev = () => setStep((s) => Math.max(s - 1, 1));

    const submit = async () => {
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                annual_kwh: form.annual_kwh ? Number(form.annual_kwh) : null,
            };
            if (form.audience === "particulier") {
                payload.company = null;
                payload.siret = null;
            }
            await api.post("/quote-requests", payload);
            setDone(true);
            toast.success("Demande envoyée — nous vous recontactons sous 24h");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="em-container py-24 max-w-2xl text-center" data-testid="quote-success">
                <div className="h-20 w-20 rounded-full bg-accent flex items-center justify-center mx-auto">
                    <CheckCircle2 className="h-10 w-10 text-accent-foreground" />
                </div>
                <h1 className="font-display text-4xl md:text-5xl font-black text-primary mt-6">{c("quote.success.title", "Demande envoyée !")}</h1>
                <p className="text-muted-foreground mt-4 leading-relaxed">
                    {c("quote.success.body", "Merci pour votre demande. Notre équipe revient vers vous sous 48h avec une proposition adaptée à votre profil.")}
                </p>
                <Button asChild className="rounded-full mt-8 bg-primary hover:bg-primary/90" size="lg">
                    <a href="/">{c("quote.success.cta", "Retour à l'accueil")}</a>
                </Button>
            </div>
        );
    }

    return (
        <div className="em-container py-16" data-testid="quote-form-page">
            <p className="em-eyebrow">{c("quote.hero.eyebrow", "Demande de devis")}</p>
            <h1 className="font-display text-4xl md:text-5xl font-black text-primary mt-2">
                {c("quote.hero.title", "Parlons de votre énergie.")}
            </h1>
            <p className="text-muted-foreground mt-3 max-w-2xl">
                {c("quote.hero.subtitle", "Renseignez votre profil en 2 minutes, on revient vers vous sous 48h avec une proposition chiffrée et sans engagement.")}
            </p>

            {/* PROGRESS */}
            <div className="mt-10 grid grid-cols-4 gap-2 max-w-3xl" data-testid="quote-progress">
                {STEPS.map((s) => (
                    <div key={s.id} className="flex flex-col gap-1.5">
                        <div className={`h-1.5 rounded-full ${s.id <= step ? "bg-primary" : "bg-secondary"}`} />
                        <p className={`text-xs font-medium ${s.id === step ? "text-primary" : "text-muted-foreground"}`}>
                            {s.id}. {s.label}
                        </p>
                    </div>
                ))}
            </div>

            <Card className="rounded-3xl border-border mt-8 max-w-3xl">
                <CardContent className="p-8 space-y-6">
                    {step === 1 && (
                        <div className="space-y-6" data-testid="quote-step-1">
                            {audienceLocked ? (
                                // Audience verrouillée par l'URL (?audience=...) — on cache le toggle
                                // et on affiche un badge qui rappelle le profil sélectionné.
                                <div className="rounded-full bg-primary/5 border border-primary/15 px-5 py-3 inline-flex items-center gap-2">
                                    <span className="text-sm">👤</span>
                                    <span className="font-semibold text-primary">
                                        {form.audience === "entreprise"
                                            ? c("quote.locked.b2b", "Je suis une entreprise")
                                            : c("quote.locked.b2c", "Je suis un particulier")}
                                    </span>
                                </div>
                            ) : (
                                <div>
                                    <Label className="em-eyebrow">{c("quote.step1.label_audience", "Vous êtes")}</Label>
                                    <Tabs value={form.audience} onValueChange={(v) => update("audience", v)} className="mt-3">
                                        <TabsList className="w-full rounded-full">
                                            <TabsTrigger value="particulier" className="flex-1 rounded-full" data-testid="quote-audience-particulier">{c("common.audience.b2c", "Particulier")}</TabsTrigger>
                                            <TabsTrigger value="entreprise" className="flex-1 rounded-full" data-testid="quote-audience-entreprise">{c("common.audience.b2b", "Entreprise")}</TabsTrigger>
                                        </TabsList>
                                    </Tabs>
                                </div>
                            )}
                            <div>
                                <Label className="em-eyebrow">{c("quote.step1.label_energy", "Énergie concernée")}</Label>
                                <Select value={form.energy_type} onValueChange={(v) => update("energy_type", v)}>
                                    <SelectTrigger className="rounded-xl mt-3" data-testid="quote-energy-type">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="electricity">{c("common.energy.elec", "Électricité")}</SelectItem>
                                        <SelectItem value="gas">{c("common.energy.gas", "Gaz")}</SelectItem>
                                        <SelectItem value="both">{c("common.energy.both", "Électricité + Gaz")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-5" data-testid="quote-step-2">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <Label htmlFor="full_name">{c("quote.step2.label_name", "Nom et prénom")}</Label>
                                    <Input id="full_name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="rounded-xl mt-2" data-testid="quote-full-name" />
                                </div>
                                <div>
                                    <Label htmlFor="phone">{c("quote.step2.label_phone", "Téléphone")}</Label>
                                    <Input id="phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="rounded-xl mt-2" data-testid="quote-phone" />
                                </div>
                                <div className="md:col-span-2">
                                    <Label htmlFor="email">{c("quote.step2.label_email", "Email")}</Label>
                                    <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="rounded-xl mt-2" data-testid="quote-email" />
                                </div>
                                {form.audience === "entreprise" && (
                                    <>
                                        <div>
                                            <Label htmlFor="company">{c("quote.step2.label_company", "Société")}</Label>
                                            <Input id="company" value={form.company} onChange={(e) => update("company", e.target.value)} className="rounded-xl mt-2" data-testid="quote-company" />
                                        </div>
                                        <div>
                                            <Label htmlFor="siret">{c("quote.step2.label_siret", "SIRET")}</Label>
                                            <Input id="siret" value={form.siret} onChange={(e) => update("siret", e.target.value)} className="rounded-xl mt-2" data-testid="quote-siret" />
                                        </div>
                                        <div>
                                            <div className="flex items-center justify-between">
                                                <Label htmlFor="pdl">N° PDL (électricité)</Label>
                                                <QuoteAssistant
                                                    fieldName="pdl"
                                                    fieldLabel="N° PDL (Point De Livraison) électricité"
                                                    currentFormData={form}
                                                    defaultQuestion="Où je trouve mon n° PDL sur ma facture ?"
                                                />
                                            </div>
                                            <Input id="pdl" value={form.pdl} onChange={(e) => update("pdl", e.target.value)} className="rounded-xl mt-2 font-mono" placeholder="14 chiffres" data-testid="quote-pdl" />
                                            <p className="text-xs text-muted-foreground mt-1">Sur votre facture Enedis (anciennement PRM).</p>
                                        </div>
                                        <div>
                                            <div className="flex items-center justify-between">
                                                <Label htmlFor="pce">N° PCE (gaz)</Label>
                                                <QuoteAssistant
                                                    fieldName="pce"
                                                    fieldLabel="N° PCE (Point Comptage Estimation) gaz"
                                                    currentFormData={form}
                                                    defaultQuestion="Où je trouve mon n° PCE sur ma facture ?"
                                                />
                                            </div>
                                            <Input id="pce" value={form.pce} onChange={(e) => update("pce", e.target.value)} className="rounded-xl mt-2 font-mono" placeholder="14 chiffres" data-testid="quote-pce" />
                                            <p className="text-xs text-muted-foreground mt-1">Sur votre facture GRDF.</p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-5" data-testid="quote-step-3">
                            {/* Upload facture → pré-remplit kWh / postal_code automatiquement */}
                            <BillUploader
                                onApply={(d) => {
                                    if (d.annual_kwh) update("annual_kwh", String(Math.round(d.annual_kwh)));
                                    if (d.energy_type) update("energy_type", d.energy_type);
                                }}
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="annual_kwh">{c("quote.step3.label_kwh", "Consommation annuelle (kWh)")}</Label>
                                        <QuoteAssistant
                                            fieldName="annual_kwh"
                                            fieldLabel="Consommation annuelle en kWh"
                                            currentFormData={form}
                                            defaultQuestion="Combien dois-je indiquer si je ne sais pas ?"
                                        />
                                    </div>
                                    <Input id="annual_kwh" type="number" value={form.annual_kwh} onChange={(e) => update("annual_kwh", e.target.value)} className="rounded-xl mt-2" data-testid="quote-annual-kwh" />
                                    <p className="text-xs text-muted-foreground mt-1">{c("quote.step3.kwh_help", "Facultatif — disponible sur votre dernière facture.")}</p>
                                </div>
                                <div>
                                    <Label htmlFor="postal_code">{c("quote.step3.label_postal", "Code postal")}</Label>
                                    <Input id="postal_code" value={form.postal_code} onChange={(e) => update("postal_code", e.target.value)} className="rounded-xl mt-2" data-testid="quote-postal-code" />
                                </div>
                            </div>
                            {form.audience === "particulier" && (
                                <div>
                                    <Label htmlFor="rfr">Revenu fiscal de référence (pour aides État)</Label>
                                    <Select value={form.revenu_fiscal || "unknown"} onValueChange={(v) => update("revenu_fiscal", v)}>
                                        <SelectTrigger className="rounded-xl mt-2"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="lt_20k">Moins de 20 000 €</SelectItem>
                                            <SelectItem value="20k_40k">20 000 – 40 000 €</SelectItem>
                                            <SelectItem value="40k_60k">40 000 – 60 000 €</SelectItem>
                                            <SelectItem value="gt_60k">Plus de 60 000 €</SelectItem>
                                            <SelectItem value="unknown">Je préfère ne pas le dire</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-muted-foreground mt-1">Détermine votre éligibilité aux aides CEE / MaPrimeRénov'.</p>
                                </div>
                            )}
                            <div>
                                <Label htmlFor="message">{c("quote.step3.label_message", "Précisions (optionnel)")}</Label>
                                <Textarea id="message" value={form.message} onChange={(e) => update("message", e.target.value)} className="rounded-xl mt-2" rows={4} data-testid="quote-message" placeholder={c("quote.step3.message_placeholder", "Travaux à venir, multi-sites, échéance, etc.")} />
                            </div>
                            <label
                                className="flex items-start gap-3 rounded-2xl border border-border p-4 cursor-pointer hover:border-primary/40 transition-colors bg-accent/10"
                                data-testid="quote-cee-interest-wrap"
                            >
                                <input
                                    type="checkbox"
                                    checked={form.cee_interest}
                                    onChange={(e) => update("cee_interest", e.target.checked)}
                                    className="mt-1 h-5 w-5 rounded border-border accent-primary"
                                    data-testid="quote-cee-interest"
                                />
                                <span className="text-sm leading-relaxed">
                                    <span className="font-bold text-primary">{c("quote.step3.cee_title", "Bonus économies — Travaux d'efficacité énergétique (CEE)")}</span>
                                    <br />
                                    {c("quote.step3.cee_body", "Cochez si vous prévoyez ou avez engagé des travaux éligibles aux Certificats d'Économies d'Énergie. Nous évaluerons votre prime potentielle.")}
                                </span>
                            </label>
                        </div>
                    )}

                    {step === 4 && (
                        <div className="space-y-4" data-testid="quote-step-4">
                            <h3 className="font-display text-2xl font-bold text-primary">{c("quote.step4.title", "Récapitulatif")}</h3>
                            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                                <div><dt className="em-eyebrow">{c("quote.recap.label_type", "Type")}</dt><dd>{form.audience} · {form.energy_type}</dd></div>
                                <div><dt className="em-eyebrow">{c("quote.recap.label_name", "Nom")}</dt><dd>{form.full_name}</dd></div>
                                <div><dt className="em-eyebrow">{c("quote.recap.label_email", "Email")}</dt><dd>{form.email}</dd></div>
                                <div><dt className="em-eyebrow">{c("quote.recap.label_phone", "Téléphone")}</dt><dd>{form.phone}</dd></div>
                                {form.company && <div><dt className="em-eyebrow">{c("quote.recap.label_company", "Société")}</dt><dd>{form.company}</dd></div>}
                                {form.siret && <div><dt className="em-eyebrow">{c("quote.recap.label_siret", "SIRET")}</dt><dd>{form.siret}</dd></div>}
                                <div><dt className="em-eyebrow">{c("quote.recap.label_postal", "Code postal")}</dt><dd>{form.postal_code}</dd></div>
                                {form.annual_kwh && <div><dt className="em-eyebrow">{c("quote.recap.label_kwh", "Conso annuelle")}</dt><dd>{form.annual_kwh} kWh</dd></div>}
                                {form.cee_interest && <div className="md:col-span-2"><dt className="em-eyebrow">{c("quote.recap.label_cee", "Bonus CEE")}</dt><dd className="font-bold text-primary">{c("quote.recap.cee_value", "Étude travaux d'économie d'énergie demandée")}</dd></div>}
                                {form.message && <div className="md:col-span-2"><dt className="em-eyebrow">{c("quote.recap.label_message", "Message")}</dt><dd>{form.message}</dd></div>}
                            </dl>
                        </div>
                    )}

                    {/* RGPD notice — toujours visible en bas */}
                    <p className="text-xs text-muted-foreground flex items-start gap-2 pt-3 border-t border-border">
                        <Lock className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        Vos données sont protégées (RGPD). Aucune transmission à des tiers sans votre accord.
                        Droits d'accès, rectification, suppression sur demande à privacy@energymarket.fr.
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-border">
                        <Button onClick={prev} disabled={step === 1} variant="ghost" className="rounded-full" data-testid="quote-prev">
                            <ArrowLeft className="h-4 w-4 mr-2" /> {c("quote.nav.prev", "Précédent")}
                        </Button>
                        {step < STEPS.length ? (
                            <Button onClick={next} className="rounded-full bg-primary hover:bg-primary/90" data-testid="quote-next">
                                {c("quote.nav.next", "Suivant")} <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        ) : (
                            <Button onClick={submit} disabled={submitting} className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold" data-testid="quote-submit">
                                <Send className="h-4 w-4 mr-2" />
                                {submitting ? "Envoi…" : c("quote.nav.submit", "Envoyer ma demande")}
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
